import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Box, alpha, useTheme, CircularProgress } from '@mui/material';
import { paths } from '@/routes/paths';
import { useOrganization } from '@/context/organization-context';
import { FEATURE_FLAGS, useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useBillingControllerGetQuota } from '@/apis/backend/generated/billing/billing';
import { UpgradeModal } from '@/components/billing/UpgradeModal';
import {
  CreateEmailSurface,
  isCreateType,
  type CreateType,
} from '@/components/email-generation/CreateEmailSurface';

// /dashboard/o/:orgId/c/:clientId/create — one surface for every channel.
//
// Deliberately NOT under /emails: the surface authors email, SMS and MMS, and
// nesting it under one of the three implied that channel was a detour from
// email rather than a peer of it. `?type=` carries the choice, so a deep link
// or a reload lands on the same channel it left. The old /emails/create path
// redirects here.
//
// The route itself is just chrome around the upgrade modal and the gradient
// background; CreateEmailSurface owns the whole flow.

interface CreateSearch {
  prompt?: string;
  autoGenerate?: boolean;
  productVariantIds?: string;
  mediaAssetIds?: string;
  type?: CreateType;
}

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/c/$clientId/create')({
  component: CreatePage,
  validateSearch: (search: Record<string, unknown>): CreateSearch => ({
    prompt: typeof search.prompt === 'string' ? search.prompt : undefined,
    autoGenerate: search.autoGenerate === true || search.autoGenerate === 'true',
    productVariantIds:
      typeof search.productVariantIds === 'string' ? search.productVariantIds : undefined,
    mediaAssetIds:
      typeof search.mediaAssetIds === 'string' ? search.mediaAssetIds : undefined,
    // An unrecognised type is dropped rather than rejected -- a bad link should
    // still open the surface on email, not fail to route.
    type: isCreateType(search.type) ? search.type : undefined,
  }),
});

function CreatePage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const search = Route.useSearch();
  const params = Route.useParams();
  const { data: quota } = useBillingControllerGetQuota(
    activeOrganization?.id ?? '',
    undefined,
    { query: { enabled: !!activeOrganization?.id } },
  );
  // The cached quota can lag the server (another tab, a comp expiring
  // mid-session), so a 403 from the create call also raises the modal.
  const [serverRefusedOnQuota, setServerRefusedOnQuota] = useState(false);
  const quotaExceeded = !!quota && !quota.canGenerate;

  // The flag map rides on the org payload and `useFeatureFlag` reads false
  // until it lands, so the redirect waits for the org: acting sooner would
  // bounce a flag-on brand off its own create page on first paint.
  const selfServeEnabled = useFeatureFlag(FEATURE_FLAGS.SELF_SERVE_BUILDER);
  const orgReady = !!activeOrganization?.id;
  const brandHome = paths.dashboard.tenant(params.orgId, params.clientId).home;

  useEffect(() => {
    if (!orgReady || selfServeEnabled) return;
    navigate({ to: brandHome, replace: true });
  }, [orgReady, selfServeEnabled, navigate, brandHome]);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: `linear-gradient(145deg, ${theme.palette.grey[50]} 0%, ${alpha(theme.palette.primary.light, 0.12)} 50%, ${theme.palette.grey[100]} 100%)`,
        px: { xs: 2, md: 3 },
        py: { xs: 1, md: 2 },
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          width: '100%',
          mx: 'auto',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {!orgReady || !selfServeEnabled ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <CreateEmailSurface
            onBack={() => navigate({ to: '/dashboard' })}
            initialPrompt={search.prompt}
            initialProductVariantIds={search.productVariantIds}
            initialMediaAssetIds={search.mediaAssetIds}
            autoGenerate={search.autoGenerate}
            initialType={search.type}
            onTypeChange={(type) =>
              navigate({
                to: '/dashboard/o/$orgId/c/$clientId/create',
                params: { orgId: params.orgId, clientId: params.clientId },
                // Replace so switching channel doesn't stack history entries the
                // user then has to click Back through.
                replace: true,
                // Email is the default, so it drops out of the URL rather than
                // being spelled out.
                search: (prev) => ({ ...prev, type: type === 'email' ? undefined : type }),
              })
            }
            quotaExceeded={quotaExceeded}
            onQuotaExceeded={() => setServerRefusedOnQuota(true)}
          />
        )}
      </Box>

      <UpgradeModal
        open={quotaExceeded || serverRefusedOnQuota}
        onClose={() => navigate({ to: '/dashboard' })}
        type="generation"
        organizationId={activeOrganization?.id ?? ''}
        used={quota?.used ?? 0}
        limit={quota?.limit ?? 0}
        nextPlanName={quota?.nextPlanName}
      />
    </Box>
  );
}
